import { useEffect, useState } from "react";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import Cookies from "js-cookie";
import { useParams } from "react-router-dom";
import { AxiosError } from "axios";
import { toast } from "sonner";

import { useEditRepaymentSchedule } from "./use-edit-repayment-schedule";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog/alert-dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover/popover";
import { Button } from "@/components/ui/button/button";
import { Calendar } from "@/components/ui/calendar/calendar";
import { cn } from "@/lib/utils";
import { editLoanRepaymentSchedule } from "@/config/mutation/admin";

export const EditRepaymentSchedule = () => {
  const queryClient = useQueryClient();

  const { loanId } = useParams() as {
    loanId: string;
  };

  const key = Cookies.get("key") as string;
  const { open, onOpenChange, rs, setRs } = useEditRepaymentSchedule();

  const [date, setDate] = useState<Date | undefined>(
    rs?.date ? new Date(rs?.date) : new Date()
  );

  const { mutateAsync, isPending } = useMutation({
    mutationFn: editLoanRepaymentSchedule(key),
  });

  const handleSubmit = async () => {
    await mutateAsync(
      {
        loanId,
        info: {
          id: rs?.id as string,
          date: date as Date,
        },
      },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            queryKey: ["other-loan", loanId],
          });

          toast.success("Schedule Updated");
          onOpenChange(false);
          setRs(null);
        },
        onError: (error) => {
          console.log("ERROR WHILE UPDATING REPAYMENT SCHEDULE", error);
          const isAxiosError = error instanceof AxiosError;
          if (isAxiosError) {
            const errorReason = error?.response?.data?.error as string;
            toast.error(errorReason);
          } else {
            toast.error(error?.message);
          }
        },
      }
    );
  };

  useEffect(() => {
    if (rs?.date) {
      setDate(new Date(rs?.date));
    }
  }, [rs]);

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Change Repayment Schedule</AlertDialogTitle>
        </AlertDialogHeader>
        <div className="flex flex-col gap-3">
          <Popover>
            <label>Due Date</label>
            <PopoverTrigger asChild>
              <Button
                variant={"outline"}
                className={cn(
                  "w-full pl-3 text-left font-normal",
                  !date && "text-muted-foreground"
                )}
              >
                {date ? format(date, "PPP") : <span>Pick a date</span>}
                <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <div>
                <Calendar mode="single" selected={date} onSelect={setDate} />
              </div>
            </PopoverContent>
          </Popover>
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel
            onClick={() => {
              setRs(null);
            }}
          >
            Cancel
          </AlertDialogCancel>
          <Button onClick={handleSubmit} disabled={isPending}>
            Continue
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
